import type { MRT_Row, MRT_TableInstance } from "mantine-react-table-open";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { MONTH_YEAR_FORMAT } from "../../../../utils/dates/dates.constants";
import type { ExcelCompatibleTableData } from "../../../../utils/files/downloadAsExcel";
import type { EnergyRevenuesCellCheckStateResponse } from "../../RevenueMonitoring.types";
import { REVENUE_TABLE_HEADER_TEXTS } from "../EnergyRevenuesTable/EnergyRevenuesTable";
import { VERIFICATION_OPTIONS } from "../EnergyRevenuesVerificationTable/columns/getStatusColumn";

/**
 * Prepares verification table data for export as a 2D array of human-readable, rounded values.
 * Uses the columns from the table instance for headers and order.
 * @param table Mantine React Table instance for verification table
 * @returns 2D array: [headers, ...rows]
 */
function prepareVerificationTableDataForExport(
  table: MRT_TableInstance<EnergyRevenuesCellCheckStateResponse>
): ExcelCompatibleTableData {
  const columns = table
    .getAllColumns()
    .filter((col) => !getColId(col).startsWith("mrt-"));
  const headers = columns.map((col) => {
    if (col.id === "diff") {
      return "Differenz in %";
    }
    return String(col.columnDef.header);
  });
  const rows = table.getPrePaginationRowModel().rows;

  const data = rows.map((row) =>
    columns.map((col) => {
      const id = getColId(col);
      return valueFormatter(id, row);
    })
  );
  return [headers, ...data];
}

function valueFormatter(
  id: string,
  row: MRT_Row<EnergyRevenuesCellCheckStateResponse>
): string {
  switch (id) {
    case "period_start":
      return backendDateOrDateTimeToLuxonDateTime(
        row.original.period_start
      ).toLocaleString(MONTH_YEAR_FORMAT);
    case "column":
      return (
        REVENUE_TABLE_HEADER_TEXTS[row.original.column] || row.original.column
      );
    case "state":
      return (
        VERIFICATION_OPTIONS.find((opt) => opt.value === row.original.state)
          ?.label || String(row.original.state)
      );
    case "user_provided_amount":
      return typeof row.original.user_provided_amount === "number"
        ? row.getValue(id)
        : "";
    case "diff": {
      const value = row.getValue(id);
      if (value === null) {
        return "";
      }
      return `${value}%`;
    }
    case "timestamp_of_state_change": {
      const value = backendDateOrDateTimeToLuxonDateTime(
        row.original.timestamp_of_state_change
      );
      return value.toLocaleString();
    }
    default: {
      return row.getValue(id) === null || row.getValue(id) === undefined
        ? ""
        : row.getValue(id);
    }
  }
}

const getColId = (col) => col.id || col.columnDef.accessorKey;

export { prepareVerificationTableDataForExport };
